import React from 'react';
import styled from 'styled-components';
import assistantClient from '@requests/telnyx/assistants.requests';

import { Command, tabActions } from '@store/slices';
import { Label } from '@common/Label';
import { NodeField } from '@components/Flow/Nodes';
import { AssistantAutocomplete } from '@common/Autocomplete/AssistantAutocomplete';
import { useQuery } from '@tanstack/react-query';
import { AIAssistant } from '@requests/telnyx';
import { RequestError } from '@requests/common';
import { useDispatch } from 'react-redux';
import { SelectContainer } from '@common/Select';
import { formatAssistantName } from '@components/Flow/Nodes/Standard/Assistant/AssistantNodeChips';
import { ErrorMessage } from '@common/Autocomplete';
import { VisibilityOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../styles';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from '@common/Tooltip';

import {
  alpha,
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  gap: 8px;
`;

const Placeholder = styled.div`
  color: ${({ theme }) => alpha(theme.text.standard, 0.4)};
  font-family: 'Inter', Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
`;

const MultiMenuItem = styled(MenuItem)<{ multiple: boolean }>``;

interface Props {
  command: Command;
  field: NodeField;
  single?: boolean;
}

export const AssistantSelector: React.FC<Props> = ({ command, field, single = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { state } = command;
  const { id, label, description } = field;
  const { service_id } = useParams();

  const { data: assistants = [], isLoading } = useQuery<AIAssistant[], RequestError>({
    queryKey: ['ai_assistants'],
    queryFn: assistantClient.list,
    retry: false,
  });

  const openAssistant = (assistantId: string) => {
    navigate(`/${service_id}/assistants/${assistantId}`);
  };

  const addAssistant = (_: React.SyntheticEvent<Element, Event>, newValue: AIAssistant | null) => {
    if (single && newValue) {
      return dispatch(
        tabActions.nodeInputUpdate({
          nodeId: command.node.id,
          fieldId: field.id,
          value: newValue.id,
        })
      );
    }

    if (newValue) {
      dispatch(
        tabActions.nodeInputArrayPush({
          nodeId: command.node.id,
          fieldId: field.id,
          value: newValue.id,
        })
      );
    }
  };

  const updateAssistants = (newValue: string[]) => {
    dispatch(
      tabActions.nodeInputUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        value: newValue,
      })
    );
  };

  if (single) {
    return (
      <Container>
        <Label title={label} tooltip={description} />
        <Stack display='grid' gridTemplateColumns={state[id] ? '1fr 40px' : '1fr'} alignItems='center' gap={0.5}>
          <AssistantAutocomplete
            value={state[id]}
            onChange={addAssistant}
            placeholder={field.placeholder}
            options={assistants}
            portal
            loading={isLoading}
          />
          {state[id] && (
            <Tooltip title='Open Assistant' arrow placement='top'>
              <IconButton
                onClick={() => openAssistant(state[id])}
                sx={{
                  border: `1px solid ${theme.line.light}`,
                  height: '40px',
                  width: '40px',
                }}
              >
                <VisibilityOutlined sx={{ fontSize: '1.4ch !important', color: theme.text.subtext }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Container>
    );
  }

  const assistantIds = state[id] as string[];

  const deletedAssistantIds = assistantIds.filter(
    (assistantId) => !assistants.some((assistant) => assistant.id === assistantId)
  );

  const deleteAssistants: AIAssistant[] = deletedAssistantIds.map((assistantId) => ({
    name: `DELETED: ${assistantId.substring(0, 24)}...`,
    id: assistantId,
    created_at: '',
    tools: [],
    description: '',
    model: '',
    instructions: '',
  }));

  return (
    <Container>
      <Label title={label} tooltip={description} />
      <SelectContainer>
        <FormControl fullWidth>
          <Select
            sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
            value={assistantIds}
            size='small'
            displayEmpty
            multiple
            onChange={(e) => updateAssistants(e.target.value as string[])}
            renderValue={(selected) => {
              if (assistantIds?.length === 0) return <Placeholder>Select assistants to route requests</Placeholder>;

              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const assistant = assistants.find((assistant) => assistant.id === value);
                    return <Chip size='small' key={value} label={formatAssistantName(assistant?.name || value)} />;
                  })}
                </Box>
              );
            }}
          >
            {assistants.concat(deleteAssistants).map((assistant) => (
              <MultiMenuItem key={assistant.id} value={assistant.id} multiple>
                <Checkbox checked={assistantIds?.includes(assistant.id)} size='small' />
                <ListItemText primary={assistant.name} />
              </MultiMenuItem>
            ))}
          </Select>
        </FormControl>
      </SelectContainer>
      {deletedAssistantIds.length > 0 && !isLoading && (
        <ErrorMessage>
          This router includes assistants that have since been deleted. Please remove them by selecting the dropdown
          above.
        </ErrorMessage>
      )}
    </Container>
  );
};
